<script lang="ts">
  import type { LeadFieldConfig, LeadsCaptureConfig } from '../lib/types';

  interface Props {
    config: LeadsCaptureConfig;
    onsubmit: (fields: Record<string, string>) => void;
    oncancel?: () => void;
    submitting?: boolean;
    error?: string | null;
    cancelLabel?: string;
  }

  let {
    config,
    onsubmit,
    oncancel,
    submitting = false,
    error = null,
    cancelLabel = 'Ahora no',
  }: Props = $props();

  // One value per field.key. Initialised lazily so re-mounts don't drop in-progress input.
  let values = $state<Record<string, string>>(initialise(config.fields));

  const title = $derived(config.title ?? '¿Querés que alguien te contacte?');
  const submitLabel = $derived(config.submitLabel ?? 'Enviar');

  function initialise(fields: LeadFieldConfig[]): Record<string, string> {
    const out: Record<string, string> = {};
    for (const f of fields) out[f.key] = '';
    return out;
  }

  function htmlInputType(type: LeadFieldConfig['type']): string {
    if (type === 'email') return 'email';
    if (type === 'phone') return 'tel';
    if (type === 'url') return 'url';
    if (type === 'number') return 'number';
    return 'text';
  }

  type AutocompleteValue = 'email' | 'tel' | 'name' | 'organization' | 'off';
  function autocompleteFor(field: LeadFieldConfig): AutocompleteValue {
    const k = field.key.toLowerCase();
    if (field.type === 'email' || k.includes('email')) return 'email';
    if (field.type === 'phone' || k.includes('phone') || k.includes('tel')) return 'tel';
    if (k === 'name' || k.includes('name')) return 'name';
    if (k.includes('company') || k.includes('organization')) return 'organization';
    return 'off';
  }

  function update(key: string, value: string): void {
    values = { ...values, [key]: value };
  }

  function handleSubmit(e: SubmitEvent): void {
    e.preventDefault();
    if (submitting) return;
    const out: Record<string, string> = {};
    for (const field of config.fields) {
      const raw = values[field.key]?.trim() ?? '';
      if (raw.length > 0) out[field.key] = raw;
    }
    onsubmit(out);
  }
</script>

<form class="hd-lead" onsubmit={handleSubmit} novalidate={false}>
  <p class="hd-lead__title">{title}</p>

  {#if error}
    <div class="hd-lead__error" role="alert">{error}</div>
  {/if}

  <div class="hd-lead__fields">
    {#each config.fields as field (field.key)}
      <label class="hd-lead__field">
        <span class="hd-lead__label">
          {field.label}
          {#if field.required}
            <span class="hd-lead__req" aria-hidden="true">*</span>
          {/if}
        </span>

        {#if field.type === 'textarea'}
          <textarea
            class="hd-lead__input hd-lead__input--textarea"
            rows="3"
            placeholder={field.placeholder ?? ''}
            required={field.required}
            disabled={submitting}
            value={values[field.key] ?? ''}
            oninput={(e) =>
              update(field.key, (e.currentTarget as HTMLTextAreaElement).value)}
            aria-required={field.required}
          ></textarea>
        {:else if field.type === 'select'}
          <select
            class="hd-lead__input"
            required={field.required}
            disabled={submitting}
            value={values[field.key] ?? ''}
            onchange={(e) =>
              update(field.key, (e.currentTarget as HTMLSelectElement).value)}
            aria-required={field.required}
          >
            <option value="" disabled>{field.placeholder ?? 'Elige una opción'}</option>
            {#each field.options ?? [] as opt (opt)}
              <option value={opt}>{opt}</option>
            {/each}
          </select>
        {:else}
          <input
            class="hd-lead__input"
            type={htmlInputType(field.type)}
            autocomplete={autocompleteFor(field)}
            placeholder={field.placeholder ?? ''}
            required={field.required}
            disabled={submitting}
            value={values[field.key] ?? ''}
            oninput={(e) =>
              update(field.key, (e.currentTarget as HTMLInputElement).value)}
            aria-required={field.required}
          />
        {/if}
      </label>
    {/each}
  </div>

  <div class="hd-lead__actions">
    {#if oncancel}
      <button
        type="button"
        class="hd-lead__ghost"
        onclick={() => oncancel?.()}
        disabled={submitting}
      >
        {cancelLabel}
      </button>
    {/if}
    <button type="submit" class="hd-lead__submit" disabled={submitting}>
      {submitting ? '…' : submitLabel}
    </button>
  </div>
</form>

<style>
  .hd-lead {
    flex: 1 1 auto;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 18px 20px 20px;
    background: var(--hd-surface, #ffffff);
    color: var(--hd-fg, #0f172a);
  }
  .hd-lead__title {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.45;
  }
  .hd-lead__error {
    font-size: 13px;
    padding: 8px 10px;
    border-radius: 8px;
    background: color-mix(in srgb, #ef4444 12%, transparent);
    color: #b91c1c;
    border: 1px solid color-mix(in srgb, #ef4444 35%, transparent);
  }
  .hd-lead__fields {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .hd-lead__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .hd-lead__label {
    font-size: 12px;
    font-weight: 500;
    color: color-mix(in srgb, var(--hd-fg, #0f172a) 70%, transparent);
  }
  .hd-lead__req {
    color: #ef4444;
    margin-left: 2px;
  }
  .hd-lead__input {
    width: 100%;
    padding: 9px 11px;
    border-radius: 8px;
    border: 1px solid var(--hd-border, #e2e8f0);
    background: var(--hd-surface, #ffffff);
    color: var(--hd-fg, #0f172a);
    font: inherit;
    font-size: 14px;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .hd-lead__input:focus {
    border-color: var(--hd-brand, #2563eb);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--hd-brand, #2563eb) 20%, transparent);
  }
  .hd-lead__input--textarea {
    resize: vertical;
    min-height: 68px;
    font-family: inherit;
  }
  .hd-lead__actions {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
  .hd-lead__ghost,
  .hd-lead__submit {
    padding: 9px 14px;
    border-radius: 8px;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid transparent;
    transition: background 0.15s ease, opacity 0.15s ease;
  }
  .hd-lead__ghost {
    background: transparent;
    color: color-mix(in srgb, var(--hd-fg, #0f172a) 70%, transparent);
    border-color: var(--hd-border, #e2e8f0);
  }
  .hd-lead__ghost:hover:not(:disabled) {
    background: color-mix(in srgb, var(--hd-fg, #0f172a) 5%, transparent);
  }
  .hd-lead__submit {
    background: var(--hd-brand, #2563eb);
    color: #ffffff;
  }
  .hd-lead__submit:hover:not(:disabled) {
    opacity: 0.92;
  }
  .hd-lead__ghost:disabled,
  .hd-lead__submit:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
</style>
